import { useState, useEffect, useCallback } from 'react'
import { customerApi } from '../services/api'
import LeadStats from '../components/leads/LeadStats'
import LeadTable from '../components/leads/LeadTable'
import LeadDetail from '../components/leads/LeadDetail'
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  CogIcon,
} from '@heroicons/react/24/outline'

export default function Leads({ onNavigate }) {
  const [leads, setLeads] = useState([])
  const [stats, setStats] = useState({ total: 0, by_status: {}, this_week: 0, this_month: 0, conversion_rate: 0 })
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0, total_pages: 1 })
  const [statusFilter, setStatusFilter] = useState('all')
  const [capturedByFilter, setCapturedByFilter] = useState('all')  // all | bot | agent
  const [search, setSearch] = useState('')
  const [selectedLead, setSelectedLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const fetchLeads = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, per_page: 20 })
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (capturedByFilter !== 'all') params.append('captured_by', capturedByFilter)
      if (search) params.append('search', search)
      const res = await customerApi.get(`/customer/leads/?${params}`)
      setLeads(res.data.leads || [])
      setPagination(res.data.pagination || { page: 1, per_page: 20, total: 0, total_pages: 1 })
    } catch (err) {
      console.error('Failed to load leads:', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, capturedByFilter, search])

  const fetchStats = useCallback(async () => {
    try {
      const res = await customerApi.get('/customer/leads/stats')
      setStats(res.data)
    } catch (err) {
      console.error('Failed to load lead stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchLeads(1)
    fetchStats()
  }, [fetchLeads, fetchStats])

  const handleFilterClick = (filterValue) => {
    setStatusFilter(filterValue)
  }

  const handlePageChange = (page) => {
    fetchLeads(page)
  }

  const handleRowClick = (lead) => {
    setSelectedLead(lead)
  }

  const handleUpdate = async (leadId, data) => {
    try {
      await customerApi.put(`/customer/leads/${leadId}`, data)
      setSelectedLead(null)
      fetchLeads(pagination.page)
      fetchStats()
    } catch (err) {
      console.error('Failed to update lead:', err)
    }
  }

  const handleDelete = async (leadId) => {
    try {
      await customerApi.delete(`/customer/leads/${leadId}`)
      setSelectedLead(null)
      fetchLeads(pagination.page)
      fetchStats()
    } catch (err) {
      console.error('Failed to delete lead:', err)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (search) params.append('search', search)
      const res = await customerApi.get(`/customer/leads/export?${params}`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'leads_export.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export leads:', err)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Lead Management</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {stats.this_week > 0 ? `${stats.this_week} new leads this week` : 'Track and manage your leads'}
            {stats.conversion_rate > 0 ? ` · ${stats.conversion_rate}% conversion rate` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          {onNavigate && (
            <button
              onClick={() => onNavigate('lead-settings')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <CogIcon className="h-4 w-4" />
              Settings
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <LeadStats stats={stats} activeFilter={statusFilter} onFilterClick={handleFilterClick} />

      {/* Search + captured-by filter row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or company..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        <div className="inline-flex items-center rounded-xl border border-slate-300 bg-white p-1 self-start">
          {[
            { v: 'all', label: 'All' },
            { v: 'bot', label: '🤖 Bot' },
            { v: 'agent', label: '👤 Agent' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setCapturedByFilter(opt.v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                capturedByFilter === opt.v
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <LeadTable
        leads={leads}
        pagination={pagination}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        loading={loading}
      />

      {/* Detail Modal */}
      {selectedLead && (
        <LeadDetail
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}
